import * as ExcelJS from 'exceljs';
import { isActionInModuleRpeCatalog } from '../rpe/rpe-module-action-catalog';
import { compareSetupMaintenancePageNames } from '../rpe/sm-rpe.constants';

export interface RolesPermissionsExportColumn {
  label: string;
  value: boolean | string | number | null;
}

export interface RolesPermissionsExportSection {
  id: string;
  label: string;
  type?: 'summary' | 'group' | 'actions' | 'action-page' | 'field-level' | 'field-tab' | 'field-legend';
  description?: string;
  columns?: RolesPermissionsExportColumn[];
  children?: RolesPermissionsExportSection[];
}

export interface RolesPermissionsExportAssignment {
  assignmentNumber: number;
  department: string;
  role: string;
  facility: string;
  information: string;
  sections: RolesPermissionsExportSection[];
}

export interface UserRolesPermissionsExportBlock {
  fullName?: string;
  summaryHeaders: string[];
  summaryValues: string[];
  rolesPermissions?: RolesPermissionsExportAssignment[];
}

/** Matches Roles UI tab order and backend export order. */
export const ROLES_PERMISSIONS_MODULE_EXPORT_ORDER: readonly string[] = [
  'Shipment',
  'Bag',
  'Manifest',
  'MAWB',
  'Account Payable',
  'Account Receivable',
  'Setup and Maintenance',
  'Other Permissions',
  'Dashboard',
  'Reports',
];

/** Modules in the Action Permissions matrix (Setup pages are extra columns). */
export const ROLES_PERMISSIONS_MATRIX_MODULE_ORDER: readonly string[] = [
  'Shipment',
  'Bag',
  'Manifest',
  'MAWB',
  'Account Payable',
  'Account Receivable',
];

/**
 * Modules with field-level permissions in Roles & Permissions UI.
 * AP, AR, and Setup & Maintenance are action-only (no field-level tab).
 */
export const ROLES_PERMISSIONS_FIELD_LEVEL_MATRIX_MODULE_ORDER: readonly string[] =
  [
    'Shipment',
    'Bag',
    'Manifest',
    'MAWB',
  ];

/** Simple-checkbox modules exported as vertical lists after field-level matrix. */
export const ROLES_PERMISSIONS_LIST_MODULE_SECTIONS: readonly {
  moduleName: string;
  sectionLabel: string;
}[] = [
  { moduleName: 'Dashboard', sectionLabel: 'Dashboard' },
  { moduleName: 'Reports', sectionLabel: 'Reports' },
  { moduleName: 'Other Permissions', sectionLabel: 'Other Permissions' },
];

const ACTION_PERMISSIONS_LABEL = 'Action Permissions';
const FIELD_LEVEL_PERMISSIONS_LABEL = 'Field Level Permissions';
const SETUP_AND_MAINTENANCE_MODULE_NAME = 'Setup and Maintenance';
const SETUP_MAINTENANCE_AGGREGATE_PAGE_NAME = 'all';
const NOT_APPLICABLE = 'N/A';
const AGGREGATE_ALL_FIELD_NAMES = new Set(['all']);
const EXCEL_SHEET_NAME_MAX_LENGTH = 31;
const INVALID_SHEET_NAME_CHARS = /[\\/?*[\]:]/g;

interface ModulePermissionData {
  moduleName: string;
  actions: Map<string, string>;
  fieldTabs: FieldTabData[];
}

interface FieldTabData {
  tabLabel: string;
  fields: FieldRowData[];
}

interface FieldRowData {
  fieldLabel: string;
  value: string;
}

interface SetupMaintenancePageData {
  pageName: string;
  actions: Map<string, string>;
}

type ActionMatrixColumn =
  | { kind: 'module'; moduleName: string }
  | { kind: 'setup-page'; pageName: string };

function permissionValue(option: RolesPermissionsExportColumn): string {
  if (typeof option.value === 'boolean') {
    return option.value ? 'Yes' : 'No';
  }
  return option.value == null ? '' : String(option.value);
}

function applyRowStyle(row: ExcelJS.Row, bold: boolean): void {
  row.eachCell(cell => {
    cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
    if (bold) {
      cell.font = { bold: true, name: 'Calibri', size: 11 };
    }
  });
}

function addStyledRow(
  worksheet: ExcelJS.Worksheet,
  values: (string | number | null | undefined)[],
  bold: boolean
): ExcelJS.Row {
  const row = worksheet.addRow(values);
  applyRowStyle(row, bold);
  return row;
}

function addBlankRow(worksheet: ExcelJS.Worksheet): void {
  worksheet.addRow([]);
}

function applyWorksheetColumnWidths(worksheet: ExcelJS.Worksheet): void {
  worksheet.columns.forEach((column, columnIndex) => {
    column.width = columnIndex === 0 ? 42 : 18;
  });
}

function normalizeModuleName(moduleName: string): string {
  return moduleName.trim().toLowerCase();
}

function getModuleSortIndex(moduleName: string): number {
  const index = ROLES_PERMISSIONS_MODULE_EXPORT_ORDER.findIndex(
    name => normalizeModuleName(name) === normalizeModuleName(moduleName)
  );
  return index >= 0 ? index : ROLES_PERMISSIONS_MODULE_EXPORT_ORDER.length + 1000;
}

function sortModuleNames(moduleNames: string[]): string[] {
  return [...moduleNames].sort(
    (left, right) => getModuleSortIndex(left) - getModuleSortIndex(right)
  );
}

function normalizeActionName(actionName: string): string {
  return actionName.trim().toLowerCase();
}

function findActionValue(
  actions: Map<string, string>,
  actionName: string
): string | undefined {
  const direct = actions.get(actionName);
  if (direct !== undefined) {
    return direct;
  }
  const target = normalizeActionName(actionName);
  for (const [key, value] of actions) {
    if (normalizeActionName(key) === target) {
      return value;
    }
  }
  return undefined;
}

function collectActionMap(
  actionSection?: RolesPermissionsExportSection
): Map<string, string> {
  const actions = new Map<string, string>();
  for (const column of actionSection?.columns ?? []) {
    actions.set(column.label.trim(), permissionValue(column));
  }
  return actions;
}

function collectFieldTabs(
  fieldLevelSection?: RolesPermissionsExportSection
): FieldTabData[] {
  const tabs: FieldTabData[] = [];

  const topLevelFields = (fieldLevelSection?.columns ?? []).map(column => ({
    fieldLabel: column.label.trim(),
    value: permissionValue(column),
  }));
  if (topLevelFields.length) {
    tabs.push({
      tabLabel: 'Fields',
      fields: topLevelFields,
    });
  }

  for (const tabSection of fieldLevelSection?.children ?? []) {
    const fields: FieldRowData[] = [];

    if (tabSection.columns?.length) {
      for (const column of tabSection.columns) {
        fields.push({
          fieldLabel: column.label.trim(),
          value: permissionValue(column),
        });
      }
    }

    for (const legendSection of tabSection.children ?? []) {
      const legendPrefix =
        legendSection.label?.trim() &&
        legendSection.label.trim().toLowerCase() !== 'fields'
          ? `${legendSection.label.trim()} - `
          : '';

      for (const column of legendSection.columns ?? []) {
        fields.push({
          fieldLabel: `${legendPrefix}${column.label.trim()}`,
          value: permissionValue(column),
        });
      }
    }

    if (fields.length) {
      tabs.push({
        tabLabel: tabSection.label.trim(),
        fields,
      });
    }
  }

  return tabs;
}

function parseAssignmentModules(
  assignment: RolesPermissionsExportAssignment
): ModulePermissionData[] {
  const modules: ModulePermissionData[] = [];

  for (const section of assignment.sections) {
    if (section.type !== 'group') {
      continue;
    }

    const actionSection = section.children?.find(child => child.type === 'actions');
    const fieldLevelSection = section.children?.find(
      child => child.type === 'field-level'
    );

    modules.push({
      moduleName: section.label.trim(),
      actions: collectActionMap(actionSection),
      fieldTabs: collectFieldTabs(fieldLevelSection),
    });
  }

  return modules.sort(
    (left, right) =>
      getModuleSortIndex(left.moduleName) - getModuleSortIndex(right.moduleName)
  );
}

function isSetupAndMaintenanceModule(moduleName: string): boolean {
  return (
    normalizeModuleName(moduleName) ===
    normalizeModuleName(SETUP_AND_MAINTENANCE_MODULE_NAME)
  );
}

function isSetupMaintenanceAggregatePage(pageName: string): boolean {
  return (
    normalizeModuleName(pageName) ===
    normalizeModuleName(SETUP_MAINTENANCE_AGGREGATE_PAGE_NAME)
  );
}

function parseSetupMaintenancePages(
  assignment: RolesPermissionsExportAssignment
): SetupMaintenancePageData[] {
  const setupGroup = assignment.sections.find(
    section =>
      section.type === 'group' && isSetupAndMaintenanceModule(section.label)
  );
  if (!setupGroup?.children?.length) {
    return [];
  }

  const pageTabs = setupGroup.children.filter(
    child =>
      child.type === 'field-tab' &&
      !!child.label?.trim() &&
      !isSetupMaintenanceAggregatePage(child.label) &&
      (child.columns?.length ?? 0) > 0
  );

  if (pageTabs.length) {
    return pageTabs
      .map(child => ({
        pageName: child.label.trim(),
        actions: collectActionMap(child),
      }))
      .sort((left, right) =>
        compareSetupMaintenancePageNames(left.pageName, right.pageName)
      );
  }

  const legacyActionSection = setupGroup.children.find(
    child => child.type === 'actions'
  );
  if (!legacyActionSection?.columns?.length) {
    return [];
  }

  return [
    {
      pageName: SETUP_AND_MAINTENANCE_MODULE_NAME,
      actions: collectActionMap(legacyActionSection),
    },
  ];
}

function buildActionMatrixColumns(
  moduleColumns: string[],
  setupPages: SetupMaintenancePageData[]
): ActionMatrixColumn[] {
  return [
    ...moduleColumns.map(
      moduleName => ({ kind: 'module', moduleName }) as ActionMatrixColumn
    ),
    ...setupPages.map(
      page => ({ kind: 'setup-page', pageName: page.pageName }) as ActionMatrixColumn
    ),
  ];
}

function getSetupMaintenancePageActionValue(
  page: SetupMaintenancePageData,
  actionName: string
): string {
  const value = findActionValue(page.actions, actionName);
  if (value !== undefined) {
    return value;
  }
  if (
    isActionInModuleRpeCatalog(SETUP_AND_MAINTENANCE_MODULE_NAME, actionName) ===
    true
  ) {
    return 'No';
  }
  return NOT_APPLICABLE;
}

function getActionMatrixCellValue(
  modules: ModulePermissionData[],
  setupPages: SetupMaintenancePageData[],
  column: ActionMatrixColumn,
  actionName: string
): string {
  if (column.kind === 'module') {
    return getActionValue(modules, column.moduleName, actionName);
  }

  const page = setupPages.find(
    setupPage =>
      normalizeModuleName(setupPage.pageName) ===
      normalizeModuleName(column.pageName)
  );
  if (!page) {
    return NOT_APPLICABLE;
  }
  return getSetupMaintenancePageActionValue(page, actionName);
}

function collectActionNames(
  modules: ModulePermissionData[],
  setupPages: SetupMaintenancePageData[] = []
): string[] {
  const actionNames: string[] = [];
  const seen = new Set<string>();

  for (const moduleName of ROLES_PERMISSIONS_MATRIX_MODULE_ORDER) {
    const moduleData = modules.find(
      module => normalizeModuleName(module.moduleName) === normalizeModuleName(moduleName)
    );
    if (!moduleData) {
      continue;
    }

    for (const actionName of moduleData.actions.keys()) {
      const key = normalizeActionName(actionName);
      if (!seen.has(key)) {
        seen.add(key);
        actionNames.push(actionName);
      }
    }
  }

  for (const moduleData of modules) {
    if (!isMatrixModuleName(moduleData.moduleName)) {
      continue;
    }
    for (const actionName of moduleData.actions.keys()) {
      const key = normalizeActionName(actionName);
      if (!seen.has(key)) {
        seen.add(key);
        actionNames.push(actionName);
      }
    }
  }

  for (const page of setupPages) {
    for (const actionName of page.actions.keys()) {
      const key = normalizeActionName(actionName);
      if (!seen.has(key)) {
        seen.add(key);
        actionNames.push(actionName);
      }
    }
  }

  return actionNames;
}

function isMatrixModuleName(moduleName: string): boolean {
  return ROLES_PERMISSIONS_MATRIX_MODULE_ORDER.some(
    name => normalizeModuleName(name) === normalizeModuleName(moduleName)
  );
}

function getActionValue(
  modules: ModulePermissionData[],
  moduleName: string,
  actionName: string
): string {
  const moduleData = modules.find(
    module => normalizeModuleName(module.moduleName) === normalizeModuleName(moduleName)
  );
  if (!moduleData) {
    return NOT_APPLICABLE;
  }

  const value = findActionValue(moduleData.actions, actionName);
  if (value !== undefined) {
    return value;
  }
  if (isActionInModuleRpeCatalog(moduleName, actionName) === true) {
    return 'No';
  }
  return NOT_APPLICABLE;
}

function isFieldLevelMatrixModuleName(moduleName: string): boolean {
  return ROLES_PERMISSIONS_FIELD_LEVEL_MATRIX_MODULE_ORDER.some(
    name => normalizeModuleName(name) === normalizeModuleName(moduleName)
  );
}

function filterMatrixModules(modules: ModulePermissionData[]): ModulePermissionData[] {
  return modules.filter(module => isMatrixModuleName(module.moduleName));
}

function getOrderedMatrixModuleColumns(
  modules: ModulePermissionData[],
  knownOrder: readonly string[],
  isKnownModule: (moduleName: string) => boolean
): string[] {
  const moduleNames = modules.map(module => module.moduleName);
  const extraModules = sortModuleNames(
    moduleNames.filter(
      moduleName =>
        isKnownModule(moduleName) &&
        !knownOrder.some(
          knownName =>
            normalizeModuleName(knownName) === normalizeModuleName(moduleName)
        )
    )
  );

  return [...knownOrder, ...extraModules];
}

function getMatrixModuleColumns(modules: ModulePermissionData[]): string[] {
  return getOrderedMatrixModuleColumns(
    modules,
    ROLES_PERMISSIONS_MATRIX_MODULE_ORDER,
    isMatrixModuleName
  );
}

function getFieldLevelMatrixModuleColumns(
  modules: ModulePermissionData[]
): string[] {
  return getOrderedMatrixModuleColumns(
    modules,
    ROLES_PERMISSIONS_FIELD_LEVEL_MATRIX_MODULE_ORDER,
    isFieldLevelMatrixModuleName
  );
}

function findModuleByName(
  modules: ModulePermissionData[],
  moduleName: string
): ModulePermissionData | undefined {
  return modules.find(
    module => normalizeModuleName(module.moduleName) === normalizeModuleName(moduleName)
  );
}

function collectListModuleRows(module: ModulePermissionData): FieldRowData[] {
  const rows: FieldRowData[] = [];
  const seen = new Set<string>();

  const addRow = (fieldLabel: string, value: string) => {
    const trimmedLabel = fieldLabel.trim();
    if (!trimmedLabel || AGGREGATE_ALL_FIELD_NAMES.has(trimmedLabel.toLowerCase())) {
      return;
    }
    const key = trimmedLabel.toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    rows.push({ fieldLabel: trimmedLabel, value });
  };

  for (const [actionName, value] of module.actions) {
    addRow(actionName, value);
  }

  for (const tab of module.fieldTabs) {
    for (const field of tab.fields) {
      addRow(field.fieldLabel, field.value);
    }
  }

  return rows;
}

function writeListModuleSection(
  worksheet: ExcelJS.Worksheet,
  sectionLabel: string,
  moduleData?: ModulePermissionData
): void {
  const rows = moduleData ? collectListModuleRows(moduleData) : [];
  if (!rows.length) {
    return;
  }

  addStyledRow(worksheet, ['', sectionLabel], true);
  for (const row of rows) {
    addStyledRow(worksheet, [row.fieldLabel, row.value], false);
  }
  addBlankRow(worksheet);
}

function addMatrixSectionHeader(
  worksheet: ExcelJS.Worksheet,
  title: string
): void {
  addStyledRow(worksheet, [title], true);
}

function writeActionMatrixHeaderRows(
  worksheet: ExcelJS.Worksheet,
  moduleColumns: string[],
  setupPages: SetupMaintenancePageData[]
): void {
  const setupPageNames = setupPages.map(page => page.pageName);

  if (!setupPageNames.length) {
    addStyledRow(worksheet, ['', ...moduleColumns], true);
    return;
  }

  const headerRow1: (string | number | null | undefined)[] = [
    '',
    ...moduleColumns,
    SETUP_AND_MAINTENANCE_MODULE_NAME,
    ...Array(Math.max(setupPageNames.length - 1, 0)).fill(''),
  ];
  const row1 = addStyledRow(worksheet, headerRow1, true);

  const headerRow2: (string | number | null | undefined)[] = [
    '',
    ...moduleColumns.map(() => ''),
    ...setupPageNames,
  ];
  addStyledRow(worksheet, headerRow2, true);

  const setupHeaderStartCol = moduleColumns.length + 2;
  const setupHeaderEndCol = setupHeaderStartCol + setupPageNames.length - 1;
  worksheet.mergeCells(row1.number, setupHeaderStartCol, row1.number, setupHeaderEndCol);
  const setupHeaderCell = row1.getCell(setupHeaderStartCol);
  setupHeaderCell.alignment = {
    horizontal: 'center',
    vertical: 'middle',
    wrapText: true,
  };
}

function writeActionPermissionsMatrix(
  worksheet: ExcelJS.Worksheet,
  modules: ModulePermissionData[],
  moduleColumns: string[],
  setupPages: SetupMaintenancePageData[]
): void {
  const actionNames = collectActionNames(modules, setupPages);
  if (!actionNames.length) {
    return;
  }

  const matrixColumns = buildActionMatrixColumns(moduleColumns, setupPages);
  if (!matrixColumns.length) {
    return;
  }

  addMatrixSectionHeader(worksheet, ACTION_PERMISSIONS_LABEL);
  writeActionMatrixHeaderRows(worksheet, moduleColumns, setupPages);

  for (const actionName of actionNames) {
    const values = matrixColumns.map(column =>
      getActionMatrixCellValue(modules, setupPages, column, actionName)
    );
    addStyledRow(worksheet, [actionName, ...values], false);
  }

  addBlankRow(worksheet);
}

function appendActionPermissionsMatrixRows(
  rows: string[][],
  modules: ModulePermissionData[],
  moduleColumns: string[],
  setupPages: SetupMaintenancePageData[]
): void {
  const actionNames = collectActionNames(modules, setupPages);
  if (!actionNames.length) {
    return;
  }

  const matrixColumns = buildActionMatrixColumns(moduleColumns, setupPages);
  if (!matrixColumns.length) {
    return;
  }

  const setupPageNames = setupPages.map(page => page.pageName);

  rows.push([ACTION_PERMISSIONS_LABEL]);
  if (setupPageNames.length) {
    rows.push([
      '',
      ...moduleColumns,
      SETUP_AND_MAINTENANCE_MODULE_NAME,
      ...Array(Math.max(setupPageNames.length - 1, 0)).fill(''),
    ]);
    rows.push(['', ...moduleColumns.map(() => ''), ...setupPageNames]);
  } else {
    rows.push(['', ...moduleColumns]);
    rows.push([]);
  }

  for (const actionName of actionNames) {
    rows.push([
      actionName,
      ...matrixColumns.map(column =>
        getActionMatrixCellValue(modules, setupPages, column, actionName)
      ),
    ]);
  }
  rows.push([]);
}

interface AggregatedFieldRow {
  tabLabel: string;
  fieldLabel: string;
  values: Map<string, string>;
}

function collectFieldMatrixRows(
  modules: ModulePermissionData[],
  moduleColumns: string[]
): AggregatedFieldRow[] {
  const rows: AggregatedFieldRow[] = [];
  const rowIndex = new Map<string, AggregatedFieldRow>();
  const tabOrder: string[] = [];
  const tabSeen = new Set<string>();
  const fieldOrderByTab = new Map<string, string[]>();
  const fieldSeenByTab = new Map<string, Set<string>>();

  const registerTab = (tabLabel: string) => {
    const key = tabLabel.toLowerCase();
    if (!tabSeen.has(key)) {
      tabSeen.add(key);
      tabOrder.push(tabLabel);
      fieldOrderByTab.set(key, []);
      fieldSeenByTab.set(key, new Set<string>());
    }
  };

  for (const moduleName of moduleColumns) {
    const moduleData = modules.find(
      module => normalizeModuleName(module.moduleName) === normalizeModuleName(moduleName)
    );
    if (!moduleData) {
      continue;
    }

    for (const tab of moduleData.fieldTabs) {
      registerTab(tab.tabLabel);
      const tabKey = tab.tabLabel.toLowerCase();
      const fieldOrder = fieldOrderByTab.get(tabKey) ?? [];
      const fieldSeen = fieldSeenByTab.get(tabKey) ?? new Set<string>();

      for (const field of tab.fields) {
        const fieldKey = field.fieldLabel.toLowerCase();
        const rowKey = `${tabKey}|${fieldKey}`;
        let row = rowIndex.get(rowKey);
        if (!row) {
          row = {
            tabLabel: tab.tabLabel,
            fieldLabel: field.fieldLabel,
            values: new Map<string, string>(),
          };
          rowIndex.set(rowKey, row);
          rows.push(row);
          if (!fieldSeen.has(fieldKey)) {
            fieldSeen.add(fieldKey);
            fieldOrder.push(field.fieldLabel);
          }
        }
        row.values.set(moduleName, field.value);
      }

      fieldOrderByTab.set(tabKey, fieldOrder);
      fieldSeenByTab.set(tabKey, fieldSeen);
    }
  }

  return tabOrder.flatMap(tabLabel => {
    const tabKey = tabLabel.toLowerCase();
    const fieldOrder = fieldOrderByTab.get(tabKey) ?? [];
    return fieldOrder
      .map(fieldLabel => {
        const rowKey = `${tabKey}|${fieldLabel.toLowerCase()}`;
        return rowIndex.get(rowKey);
      })
      .filter((row): row is AggregatedFieldRow => !!row);
  });
}

function writeFieldLevelPermissionsMatrix(
  worksheet: ExcelJS.Worksheet,
  modules: ModulePermissionData[],
  moduleColumns: string[]
): void {
  const fieldRows = collectFieldMatrixRows(modules, moduleColumns);
  if (!fieldRows.length) {
    return;
  }

  addMatrixSectionHeader(worksheet, FIELD_LEVEL_PERMISSIONS_LABEL);
  addStyledRow(worksheet, ['', ...moduleColumns], true);

  let currentTab = '';
  for (const fieldRow of fieldRows) {
    if (fieldRow.tabLabel !== currentTab) {
      currentTab = fieldRow.tabLabel;
      addStyledRow(worksheet, [currentTab], true);
    }

    const values = moduleColumns.map(moduleName => {
      const moduleData = modules.find(
        module =>
          normalizeModuleName(module.moduleName) === normalizeModuleName(moduleName)
      );
      if (!moduleData) {
        return NOT_APPLICABLE;
      }
      return fieldRow.values.get(moduleName) ?? NOT_APPLICABLE;
    });

    addStyledRow(worksheet, [fieldRow.fieldLabel, ...values], false);
  }

  addBlankRow(worksheet);
}

function writeSummarySection(
  worksheet: ExcelJS.Worksheet,
  section: RolesPermissionsExportSection
): void {
  addStyledRow(
    worksheet,
    (section.columns ?? []).map(column => column.label),
    true
  );
  addStyledRow(
    worksheet,
    (section.columns ?? []).map(column => permissionValue(column)),
    false
  );
}

function writeHorizontalOptionTable(
  worksheet: ExcelJS.Worksheet,
  columns: RolesPermissionsExportColumn[]
): void {
  if (!columns.length) {
    return;
  }
  addStyledRow(
    worksheet,
    columns.map(column => column.label),
    true
  );
  addStyledRow(
    worksheet,
    columns.map(column => permissionValue(column)),
    false
  );
}

/** Matches nested print preview: Action | Permission pairs side-by-side. */
function writeActionPermissionsPrintStyle(
  worksheet: ExcelJS.Worksheet,
  columns: RolesPermissionsExportColumn[]
): void {
  if (!columns.length) {
    return;
  }
  addStyledRow(worksheet, ['Action', 'Permission', 'Action', 'Permission'], true);
  for (let index = 0; index < columns.length; index += 2) {
    const left = columns[index];
    const right = columns[index + 1];
    addStyledRow(
      worksheet,
      [
        left.label,
        permissionValue(left),
        right?.label ?? '',
        right ? permissionValue(right) : '',
      ],
      false
    );
  }
}

function writeFieldLevelPrintStyle(
  worksheet: ExcelJS.Worksheet,
  fieldLevelSection: RolesPermissionsExportSection
): void {
  addStyledRow(worksheet, [fieldLevelSection.label], true);
  if (fieldLevelSection.description) {
    addStyledRow(
      worksheet,
      [`Field Level Permissions (${fieldLevelSection.description})`],
      false
    );
  }

  writeHorizontalOptionTable(worksheet, fieldLevelSection.columns ?? []);

  for (const tabSection of fieldLevelSection.children ?? []) {
    if (!tabSection.children?.length) {
      if (
        tabSection.label?.trim() &&
        tabSection.label.trim().toLowerCase() !== 'fields'
      ) {
        addStyledRow(worksheet, [tabSection.label], true);
      }
      writeHorizontalOptionTable(worksheet, tabSection.columns ?? []);
      continue;
    }

    for (const legendSection of tabSection.children) {
      if (
        legendSection.label?.trim() &&
        legendSection.label.trim().toLowerCase() !== 'fields'
      ) {
        addStyledRow(worksheet, [legendSection.label], true);
      }
      writeHorizontalOptionTable(worksheet, legendSection.columns ?? []);
    }
  }
}

function writeModuleGroupPrintStyle(
  worksheet: ExcelJS.Worksheet,
  moduleSection: RolesPermissionsExportSection
): void {
  addStyledRow(worksheet, [moduleSection.label], true);

  for (const child of moduleSection.children ?? []) {
    if (child.type === 'actions') {
      addStyledRow(worksheet, [child.label], true);
      if (child.columns?.length) {
        writeActionPermissionsPrintStyle(worksheet, child.columns);
      }
      for (const pageSection of child.children ?? []) {
        if (pageSection.label?.trim()) {
          addStyledRow(worksheet, [pageSection.label], true);
        }
        writeActionPermissionsPrintStyle(worksheet, pageSection.columns ?? []);
      }
      addBlankRow(worksheet);
      continue;
    }

    if (child.type === 'field-level') {
      writeFieldLevelPrintStyle(worksheet, child);
      addBlankRow(worksheet);
    }
  }
}

/**
 * Module-wise layout matching Roles & Permissions nested print preview
 * (Department summary, then each module with its Action / Field Level blocks).
 * Distinct from the combined matrix used for User R&P export.
 */
export function writeRolesPermissionsModuleWiseToWorksheet(
  worksheet: ExcelJS.Worksheet,
  assignment: RolesPermissionsExportAssignment
): void {
  for (const section of assignment.sections) {
    if (section.type === 'summary') {
      addStyledRow(worksheet, [section.label], true);
      writeSummarySection(worksheet, section);
      addBlankRow(worksheet);
      continue;
    }

    if (section.type === 'group') {
      writeModuleGroupPrintStyle(worksheet, section);
      addBlankRow(worksheet);
    }
  }

  worksheet.columns.forEach((column, columnIndex) => {
    column.width = columnIndex === 0 ? 36 : 18;
  });
}

export function writeRolesPermissionsAssignmentToWorksheet(
  worksheet: ExcelJS.Worksheet,
  assignment: RolesPermissionsExportAssignment,
  options?: { includeAssignmentHeading?: boolean }
): void {
  if (options?.includeAssignmentHeading !== false) {
    addStyledRow(
      worksheet,
      [`Roles & Permissions ${assignment.assignmentNumber}`],
      true
    );
  }

  const summarySection = assignment.sections.find(section => section.type === 'summary');
  if (summarySection) {
    writeSummarySection(worksheet, summarySection);
    addBlankRow(worksheet);
  }

  const modules = parseAssignmentModules(assignment);
  const setupPages = parseSetupMaintenancePages(assignment);
  const matrixModules = filterMatrixModules(modules);
  const actionMatrixColumns = getMatrixModuleColumns(matrixModules);
  const fieldLevelMatrixColumns = getFieldLevelMatrixModuleColumns(matrixModules);

  writeActionPermissionsMatrix(
    worksheet,
    matrixModules,
    actionMatrixColumns,
    setupPages
  );
  writeFieldLevelPermissionsMatrix(
    worksheet,
    matrixModules,
    fieldLevelMatrixColumns
  );

  for (const section of ROLES_PERMISSIONS_LIST_MODULE_SECTIONS) {
    writeListModuleSection(
      worksheet,
      section.sectionLabel,
      findModuleByName(modules, section.moduleName)
    );
  }
}

export function writeUserSummaryTableToWorksheet(
  worksheet: ExcelJS.Worksheet,
  users: UserRolesPermissionsExportBlock[]
): void {
  if (!users.length) {
    return;
  }

  addStyledRow(worksheet, users[0].summaryHeaders, true);
  for (const user of users) {
    addStyledRow(worksheet, user.summaryValues, false);
  }

  applyWorksheetColumnWidths(worksheet);
}

export function writeUserRolesPermissionsOnlyToWorksheet(
  worksheet: ExcelJS.Worksheet,
  user: UserRolesPermissionsExportBlock
): void {
  for (const assignment of user.rolesPermissions ?? []) {
    writeRolesPermissionsAssignmentToWorksheet(worksheet, assignment);
  }

  applyWorksheetColumnWidths(worksheet);
}

export function writeUserRolesPermissionsExportToWorksheet(
  worksheet: ExcelJS.Worksheet,
  users: UserRolesPermissionsExportBlock[]
): void {
  users.forEach((user, index) => {
    if (index > 0) {
      addBlankRow(worksheet);
    }

    addStyledRow(worksheet, user.summaryHeaders, true);
    addStyledRow(worksheet, user.summaryValues, false);
    addBlankRow(worksheet);

    for (const assignment of user.rolesPermissions ?? []) {
      writeRolesPermissionsAssignmentToWorksheet(worksheet, assignment);
    }
  });

  applyWorksheetColumnWidths(worksheet);
}

export function sanitizeExcelWorksheetName(
  proposedName: string,
  usedNames: Set<string> = new Set<string>()
): string {
  const baseName = (proposedName || 'User')
    .replace(INVALID_SHEET_NAME_CHARS, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, EXCEL_SHEET_NAME_MAX_LENGTH);

  let candidate = baseName || 'User';
  let suffix = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    const suffixText = ` (${suffix})`;
    const trimmedBase = baseName.slice(
      0,
      Math.max(1, EXCEL_SHEET_NAME_MAX_LENGTH - suffixText.length)
    );
    candidate = `${trimmedBase}${suffixText}`;
    suffix += 1;
  }

  usedNames.add(candidate.toLowerCase());
  return candidate;
}

export function flattenRolesPermissionsAssignmentToRows(
  assignment: RolesPermissionsExportAssignment
): string[][] {
  const rows: string[][] = [];
  rows.push([`Roles & Permissions ${assignment.assignmentNumber}`]);

  const summarySection = assignment.sections.find(section => section.type === 'summary');
  if (summarySection) {
    rows.push((summarySection.columns ?? []).map(column => column.label));
    rows.push((summarySection.columns ?? []).map(column => permissionValue(column)));
    rows.push([]);
  }

  const modules = parseAssignmentModules(assignment);
  const setupPages = parseSetupMaintenancePages(assignment);
  const matrixModules = filterMatrixModules(modules);
  const actionMatrixColumns = getMatrixModuleColumns(matrixModules);
  const fieldLevelMatrixColumns = getFieldLevelMatrixModuleColumns(matrixModules);

  appendActionPermissionsMatrixRows(
    rows,
    matrixModules,
    actionMatrixColumns,
    setupPages
  );

  const fieldRows = collectFieldMatrixRows(matrixModules, fieldLevelMatrixColumns);
  if (fieldRows.length && fieldLevelMatrixColumns.length) {
    rows.push([FIELD_LEVEL_PERMISSIONS_LABEL]);
    rows.push(['', ...fieldLevelMatrixColumns]);
    let currentTab = '';
    for (const fieldRow of fieldRows) {
      if (fieldRow.tabLabel !== currentTab) {
        currentTab = fieldRow.tabLabel;
        rows.push([currentTab]);
      }
      rows.push([
        fieldRow.fieldLabel,
        ...fieldLevelMatrixColumns.map(
          moduleName => fieldRow.values.get(moduleName) ?? NOT_APPLICABLE
        ),
      ]);
    }
    rows.push([]);
  }

  for (const section of ROLES_PERMISSIONS_LIST_MODULE_SECTIONS) {
    const moduleData = findModuleByName(modules, section.moduleName);
    const listRows = moduleData ? collectListModuleRows(moduleData) : [];
    if (!listRows.length) {
      continue;
    }
    rows.push(['', section.sectionLabel]);
    for (const listRow of listRows) {
      rows.push([listRow.fieldLabel, listRow.value]);
    }
    rows.push([]);
  }

  return rows;
}

export { permissionValue };
